import React, { useState, useEffect, Suspense } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { motion } from "framer-motion";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  LogIn,
  AlertCircle,
  CheckCircle,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";

useGLTF.preload("/models/2014_lamborghini_veneno_roadster.glb");

function Model() {
  const { scene } = useGLTF("/models/2014_lamborghini_veneno_roadster.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

function CarScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      style={{ width: "100%", height: "100%" }}
      className="w-full h-full"
    >
      <color attach="background" args={["#0f172a"]} />
      <Suspense fallback={null}>
        <Stage environment="city">
          <Model />
        </Stage>
        <Environment preset="city" />
        <OrbitControls autoRotate autoRotateSpeed={0.5} enableZoom={false} />
      </Suspense>
    </Canvas>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState("");

  const { login, error: authError } = useAuth();
  const navigate = useNavigate();

  const iconVariants = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
      },
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    setIsLoading(true);

    if (!email || !password) {
      setLocalError("Veuillez remplir tous les champs");
      setIsLoading(false);
      return;
    }

    try {
      const result = await login(email, password);

      if (result.success) {
        setEmail("");
        setPassword("");
        navigate("/");
      } else {
        setLocalError(result.error || "Erreur de connexion");
      }
    } catch (error) {
      setLocalError("Une erreur est survenue. Veuillez réessayer.");
      console.error("Erreur login:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = localError || authError;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4 py-32 relative overflow-hidden">
      {/* Canvas 3D */}
      <div className="fixed inset-0 z-0" style={{ zIndex: 0 }}>
        <CarScene />
      </div>

      {/* Loader */}
      <Loader />

      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-1" style={{ zIndex: 1 }} />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="w-full max-w-md relative z-10"
        style={{ zIndex: 10 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="inline-block mb-4"
          >
            <div className="bg-gradient-to-br from-blue-600 to-blue-500 p-4 rounded-full shadow-lg shadow-blue-500/50">
              <LogIn className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-3xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-300"
          >
            Connexion
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-slate-400 text-xs"
          >
            Bienvenue sur votre plateforme
          </motion.p>
        </div>

        {/* Formulaire Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-slate-900/80 backdrop-blur-xl p-6 rounded-2xl shadow-2xl border border-slate-700/50"
        >
          {/* Messages */}
          {displayError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-3 rounded-lg mb-4 flex items-center gap-2 text-xs"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{displayError}</span>
            </motion.div>
          )}

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label className="flex text-xs font-medium text-white mb-2 items-center gap-2">
                <motion.div variants={iconVariants} animate="animate">
                  <Mail className="w-4 h-4 text-blue-400" />
                </motion.div>
                Votre email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all text-sm disabled:opacity-50"
                placeholder="vous@exemple.com"
                required
              />
            </motion.div>

            {/* Password */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <label className="flex text-xs font-medium text-white mb-2 items-center gap-2">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Lock className="w-4 h-4 text-blue-400" />
                </motion.div>
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all pr-10 text-sm disabled:opacity-50"
                  placeholder="••••••••"
                  required
                />
                <motion.button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 disabled:opacity-50"
                  whileHover={{ scale: 1.1 }}
                >
                  {showPassword ? (
                    <Eye className="w-4 h-4" />
                  ) : (
                    <EyeOff className="w-4 h-4" />
                  )}
                </motion.button>
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isLoading}
              className="w-full mt-8 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-2.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed text-sm"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Connexion...</span>
                </>
              ) : (
                <>
                  <span>Se connecter</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </motion.button>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-slate-600/30" />
            <span className="text-slate-500 text-xs">ou</span>
            <div className="flex-1 h-px bg-slate-600/30" />
          </div>

          {/* Register Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center mb-3"
          >
            <p className="text-slate-400 text-xs">
              Pas encore de compte ?{" "}
              <Link
                to="/register"
                className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
              >
                S'inscrire
              </Link>
            </p>
          </motion.div>

          {/* Forgot Password */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center"
          >
            <Link
              to="/reset-password"
              className="text-slate-400 hover:text-slate-300 text-xs transition-colors"
            >
              Mot de passe oublié ?
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}